import React from 'react';
import { Link } from 'react-router-dom';
import { createUseStyles } from 'react-jss';
import { S } from './tokens';

export type TabItem = { key: string; label: string; to: string; count?: number | null; active: boolean; testId?: string };

const useStyles = createUseStyles({
  root: {
    display: 'flex',
    gap: 4,
    marginBottom: 18,
    // The line is drawn inside, so the active tab's underline can sit on it without overflowing.
    boxShadow: `inset 0 -1px 0 ${S.line}`,
    overflowX: 'auto',
    overflowY: 'hidden',
    fontFamily: S.ui
  },
  tab: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    height: 40,
    padding: '0 12px',
    fontSize: 14,
    fontWeight: 500,
    color: S.muted,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    borderRadius: '8px 8px 0 0',
    '&:hover': { color: S.fg, background: S.chrome },
    // Inside the tab: the bar clips anything drawn outside it.
    '&:focus-visible': { outline: 'none', boxShadow: `inset 0 0 0 2px ${S.fg}` },
    '&[aria-current="page"]': {
      color: S.fg,
      fontWeight: 600,
      '&::after': { content: '""', position: 'absolute', left: 8, right: 8, bottom: 0, height: 2, borderRadius: 2, background: S.fg }
    }
  },
  count: {
    minWidth: 20,
    height: 20,
    padding: '0 6px',
    boxSizing: 'border-box',
    borderRadius: 10,
    background: S.chromeHover,
    color: S.fg,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: '20px',
    textAlign: 'center',
    fontVariantNumeric: 'tabular-nums'
  }
});

/** Views of one screen, each with its own address. */
const Tabs: React.FC<{ items: TabItem[]; label: string }> = ({ items, label }) => {
  const c = useStyles();
  return (
    <nav className={c.root} aria-label={label}>
      {items.map(t => (
        <Link key={t.key} to={t.to} className={c.tab} aria-current={t.active ? 'page' : undefined} data-testid={t.testId}>
          {t.label}
          {t.count != null && t.count > 0 && <span className={c.count}>{t.count}</span>}
        </Link>
      ))}
    </nav>
  );
};

export default Tabs;
